import React, { useState, useEffect, useMemo } from "react";

// Constants & utilities
import {
  SUMMARY_VARIABLES_HPMS,
  SUMMARY_VARIABLES_TOP100,
  SUMMARY_VARIABLES_ROUTE,
  SUMMARY_VARIABLES_ROUTE_100,
  SUMMARY_VARIABLES_ROUTE_100_DIRECTIONAL,
  SUMMARY_VARIABLES_GROUPING,
} from "../common/constants";
import { getCurrentDate } from "../utils/utils";

// Context
import { useGlobalState, useGlobalUpdater } from "../common/GlobalContext";

// Material UI  core
import { useTheme } from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Tooltip from "@material-ui/core/Tooltip";
import SaveAlt from "@material-ui/icons/SaveAlt";
import MapIcon from "@material-ui/icons/Map";

// Material UI  MUI v5
import TablePagination from "@material-ui/core/TablePagination";

// Table
import MaterialTable from "@material-table/core";
import { MTableHeader, MTableAction } from "material-table";

// Local components
import TableColumnSelector from "./TableColumnSelector";
import ExportFileNameDialog from "../utils/ExportFileNameDialog";
import TableTopScrollbar from "./TableTopScrollbar";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  exportButton: {
    marginLeft: theme.spacing(2),
  },

  "@global": {
    ".table-wrap .MuiTableCell-head": {
      position: "sticky",
      top: 0,
      zIndex: 5,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,                // follows light/dark
    },
    ".MuiTableSortLabel-icon": {
      opacity: 0.4,
    },
    ".MuiTableSortLabel-root:focus": {
      outline: "2px solid #1976d2",
      outlineOffset: "2px",
      borderRadius: "2px",
    },
    ".MuiTableSortLabel-root:focus-visible": {
      outline: "2px solid #1976d2",
      outlineOffset: "2px",
      borderRadius: "2px",
    },
  },

}));

export default function SummaryTable(props) {
  const classes = useStyles();
  const theme = useTheme();
  const query = useGlobalState();
  const dispatch = useGlobalUpdater();
  const [grouping, setGrouping] = useState("route_id");
  const [selectedRows, setSelectedRows] = useState([]);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const tableRef = React.createRef();
  const [tableData, setTableData] = useState(query.features);
  const prevAutoDefaultKeyRef = React.useRef(null);
  const tableTitle = ` ${query.layerSpecs.layer} ${query.yearSelected} Summary`;

  useEffect(() => {
    setTableData(query.features);
  }, [query.features]);

  function groupBy(xs, key) {
    return xs.reduce(function (rv, x) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  }

  function filterHPMSVariables(category) {
    let results = [];
    let SUMMARY_VARIABLES_layer;
    if (
      query.layerSpecs.category !== "Top 100"
    ) {
      SUMMARY_VARIABLES_layer = SUMMARY_VARIABLES_HPMS;
    } else {
      SUMMARY_VARIABLES_layer = SUMMARY_VARIABLES_TOP100;
    }

    for (var i = SUMMARY_VARIABLES_layer.length - 1; i >= 0; i--) {
      if (
        category === SUMMARY_VARIABLES_layer[i].category &&
        SUMMARY_VARIABLES_layer[i].variable !== " commodity_value"
      ) {
        results.push(SUMMARY_VARIABLES_layer[i]);
      }
    }

    return results;
  }


  const [columns, setColumns] = useState(query.tableColumnsTop100);
  useEffect(() => {
    let cols = [];
    let colVars = [];

    if (query.layerSpecs.category === "Top 100") {
      if (query.layerSpecs.layer.split(" ")[0] === "Directional")
        colVars = SUMMARY_VARIABLES_ROUTE_100_DIRECTIONAL;
      else
        colVars = SUMMARY_VARIABLES_ROUTE_100;

      for (var i = colVars.length - 1; i >= 0; i--) {
        cols.push(colVars[i]);
      }

      // Use query.tableColumnsTop100 directly as source of truth
      cols = cols.concat(
        query.tableColumnsTop100.filter((itm) => itm.category !== "route")
      );

    } else {
      if (grouping === "route_id") {
        colVars = SUMMARY_VARIABLES_ROUTE;
      } else {
        colVars.push(
          SUMMARY_VARIABLES_GROUPING.find((value) => value.id === grouping)
        );
      }
      for (var i = colVars.length - 1; i >= 0; i--) {
        cols.push(colVars[i]);
      }
      cols = cols.concat(
        query.tableColumnsHPMS.filter((itm) => itm.category !== "route")
      );
    }

    setColumns(cols);

    // Build the actual MaterialTable columns from checked state
    const tableColumnsTemp = [];
    cols.forEach((headCell) => {
      if (!headCell.checked) return;
      tableColumnsTemp.push({
        title: headCell.tooltip ? (
          <Tooltip title={headCell.tooltip} placement="top" arrow>
            <span>{headCell.label}</span>
          </Tooltip>
        ) : headCell.label,
        field: headCell.id,
        cellStyle: {
          borderTopWidth: 0, borderBottomWidth: 1, borderLeftWidth: 0,
          borderRightWidth: 2, borderColor: "rgba(224, 224, 224, 0.5)", borderStyle: "solid",
        },
        headerStyle: {
          borderTopWidth: 0, borderBottomWidth: 1, borderLeftWidth: 0,
          borderRightWidth: 2, borderColor: "rgba(224, 224, 224, 0.5)", borderStyle: "solid",
        },
        render: (rowData) =>
          rowData[headCell.id] !== null &&
            typeof rowData[headCell.id] !== "undefined" &&
            rowData[headCell.id] !== "undefined"
            ? rowData[headCell.id].toLocaleString("en-US", headCell.formatOptions)
            : "",
      });
    });

    setTableColumns(tableColumnsTemp);


  }, [query.layerSpecs.layer, query.vehicleType, grouping, query.tableColumnsTop100, query.tableColumnsHPMS]);

  useEffect(() => {
    if (query.layerSpecs.category !== "Top 100") return;

    const key = `${query.layerSpecs.layer}||${query.vehicleType}`;
    if (prevAutoDefaultKeyRef.current === key) return;
    prevAutoDefaultKeyRef.current = key;

    const isTruckLayer =
      query.layerSpecs.layer.includes("Truck") || query.vehicleType === "truck";

    dispatch({
      type: "setTableColumnsTop100",
      selection: query.tableColumnsTop100.map((itm) => ({
        ...itm,
        checked:
          itm.category === "route"
            ? itm.checked
            : isTruckLayer
              ? itm.category === "truck"
              : itm.category === "all",
      })),
    });
  }, [query.layerSpecs.layer, query.vehicleType]);

  function buildColsState(cols, category, state) {
    columns
      .filter((item) => item.category === category)
      .map(
        (col, index) =>
        (cols = {
          ...cols,
          [col.id]: state === "keep" ? columnsState[col.id] : state,
        })
      );

    return cols;
  }

  const [columnsState, setColumnsState] = useState(query.tableColumns);
  useEffect(() => {
    let cols = {};

    cols = buildColsState(cols, "route", true);
    cols = buildColsState(cols, "all", true);
    cols = buildColsState(cols, "truck", false);

    dispatch({
      type: "setTableColumns",
      selection: cols,
    });
  }, [query.layerSpecs.category, grouping]);

  useEffect(() => {
    setColumnsState(query.tableColumns);
  }, [query.tableColumns, grouping]);

  useEffect(() => {
    if (
      query.layerSpecs.category === "Top 100" &&
      query.directionality !== "crossSectional"
    ) {
      let temColState = query.tableColumns;
      temColState.direction = true;
      setColumnsState(temColState);
    }
  }, [
    query.layerSpecs.category,
    query.directionality,
  ]);

  function buildColsNew(type) {
    let cols = [];

    const cellStyleBase = {
      padding: "0px 4px",
      borderTopWidth: 0, borderBottomWidth: 1, borderLeftWidth: 0,
      borderRightWidth: 2, borderColor: "rgba(224, 224, 224, 0.5)", borderStyle: "solid",
    };

    const headerStyleBase = {
      padding: "0px 4px",
      position: "sticky",
      top: 0,
      zIndex: 3,
      backgroundColor: theme.palette.background.paper,
      borderTopWidth: 0, borderBottomWidth: 1, borderLeftWidth: 0,
      borderRightWidth: 2, borderColor: "rgba(224, 224, 224, 0.5)", borderStyle: "solid",
    };

    cols.push({
      title: "Reporting Year",
      field: "year",
      cellStyle: cellStyleBase,
      headerStyle: headerStyleBase,
      render: (rowData) => (rowData && rowData.year != null ? rowData.year : ""),
    });

    columns.map(
      (headCell) =>
        headCell.checked &&
        cols.push({
          title: headCell.tooltip ? (
            <Tooltip title={headCell.tooltip} placement="top" arrow>
              <span>{headCell.label}</span>
            </Tooltip>
          ) : headCell.label,
          field: headCell.id,
          cellStyle: cellStyleBase,
          headerStyle: headerStyleBase,
          render: (rowData) =>
            rowData[headCell.id] !== null &&
              typeof rowData[headCell.id] !== "undefined" &&
              rowData[headCell.id] !== "undefined"
              ? rowData[headCell.id].toLocaleString("en-US", headCell.formatOptions)
              : "",
        })
    );

    return cols;
  }

  function aggrigateRow(rawRow) {
    let row = {};

    columns.map((colVars, index) => {
      let column = colVars.id;
      // check to see if normalization is needed.
      if (
        colVars.checked &&
        typeof colVars.normalizedBy !== "undefined" &&
        colVars.normalizedBy
      ) {
        let sum = 0;
        let normSum = 0;
        for (var i = rawRow.length - 1; i >= 0; i--) {
          sum += rawRow[i][column] * rawRow[i][colVars.normalizedBy];
          normSum += rawRow[i][colVars.normalizedBy];
        }

        if (normSum === 0) row[column] = 0;
        else row[column] = sum / normSum;
        // standard aggregate.
      } else if (
        colVars.checked &&
        typeof colVars.normalizedBy !== "undefined" &&
        !colVars.normalizedBy
      ) {
        row[column] = rawRow.reduce(function (prev, cur) {
          return prev + cur[column];
        }, 0);
        // Text only.
      } else if (colVars.checked) {
        let label = false;
        let col = columns.find((c) => column === c.id);

        if (
          typeof col !== "undefined" &&
          typeof col.queryVariable !== "undefined"
        ) {
          label = query[col.queryVariable].find(
            (value) => value.id == rawRow[0][column]
          );
        }

        if (label) {
          row[column] = label.label;
        } else {
          row[column] = rawRow[0][column];
        }
      }
    });

    return row;
  }

  function buildRow(rawRow) {
    let row = {};
    columns.map((colVars, index) => {
      let column = colVars.id;

      if (colVars.checked) {
        if (column === "trkvolper") {
          row[column] = rawRow["aadt"] > 0
            ? (rawRow["trkaadt"] / rawRow["aadt"]) * 100
            : 0;
        }
        // Truck Congested Cost: old years = trkcost, 2023/2024 = commcost
        else if (column === "trkcost") {
          row[column] = rawRow["trkcost"] ?? rawRow["commcost"] ?? null;

          // All Vehicle Congested Cost: old years = tlcost, 2023/2024 = congcost
        } else if (column === "tlcost") {
          row[column] = rawRow["tlcost"] ?? rawRow["congcost"] ?? null;
        }
        else {
          row[column] = rawRow[column];
        }
      }
    });

    if (query.layerSpecs.category === "Top 100") {
      if (rawRow.direction) row.id = rawRow.mergekey + "-" + rawRow.direction;
      else row.id = rawRow.mergekey;
      row.year = rawRow.year_recor ?? query.yearSelected;
    } else {
      row.id = rawRow.id;
    }

    return row;
  }

  function buildRows() {
    let rows = [];
    if (query.layerSpecs.category !== "Top 100") {

      let rowsGroup = groupBy(tableData, grouping);
      for (const group in rowsGroup) {
        let row = aggrigateRow(rowsGroup[group]);
        row.segments = rowsGroup[group];
        row.id = row.segments[0].id;
        row.trkvolper = (row.trkaadt / row.aadt) * 100;
        row.segmentCount = rowsGroup[group].length;
        row.year = rowsGroup[group][0].year_recor ?? query.yearSelected;
        if (grouping === "route_id") {
          row.end_point = row.segments[row.segments.length - 1].end_point;
        }

        rows.push(row);
      }

    } else {
      let rawRows = tableData;

      for (var i = rawRows.length - 1; i >= 0; i--) {
        rows.push(buildRow(rawRows[i]));
      }
    }

    return rows;
  }
  //avoids redering on every change, only when tableData, columns, grouping, or category changes
  const rows = useMemo(() => buildRows(), [tableData, columns, grouping, query.layerSpecs.category]);

  function handleChange(e) {
    setGrouping(e.target.value);
  }

  const isRoadSelected = (selectedRows, feature) => {
    return selectedRows.includes(feature.properties.id);

  };

  const updateSelectedRows = (srows) => {

    dispatch({
      type: "setUserSelectedRoads",
      selection: srows,
    });
  };

  const handleClick = (data) => {
    setSelectedRows(data);
    if (data.length > 0) {
      if (
        query.layerSpecs.category === "Top 100"
      ) {
        let newSelecteds = data.map((n) => n.id);
        if (query.roadLayerType === "MD100-Directional")
          newSelecteds = data.map((n) => n.id.substring(0, n.id.length - 3));

        updateSelectedRows(newSelecteds);
      } else {
        let segmentIds = [];
        for (var d = data.length - 1; d >= 0; d--) {
          for (var ds = data[d].segments.length - 1; ds >= 0; ds--) {
            segmentIds.push(data[d].segments[ds].id);
          }
        }

        updateSelectedRows(segmentIds);
      }

      return;
    }

    updateSelectedRows([]);
  };
  useEffect(() => {
    setSelectedRows([]);
    updateSelectedRows([]);
  }, [query.layerSpecs.category,]);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [csvFileNameTest, setCsvFileNameTest] = useState("myTable");
  const [exportCols, setExportCols] = useState();
  const [exportData, setExportData] = useState();

  const TABLE_ICONS = {
    Export: () => <SaveAlt fontSize="large" className={classes.exportButton} />,
  };

  const [tableColumns, setTableColumns] = useState(buildColsNew("full_table"));

  return (
    <div className={classes.root}>
      {rows.length !== null && (
        <Paper className={classes.paper + " table-wrap"}>
          <div style={{ position: 'relative', float: 'right', marginTop: '8px', marginRight: '8px', zIndex: 1 }}>
            <TableColumnSelector
              open={columnSelectorOpen}
              setOpen={setColumnSelectorOpen}
            />
          </div>
          <TableTopScrollbar ariaLabel="Summary table, scroll horizontally">
            <MaterialTable
              title={tableTitle}
              columns={buildColsNew("full_table")}

              data={rows}

              localization={{
                header: {
                  actions: "Actions",
                },
              }}
              tableRef={tableRef}

              options={{
                exportButton: true,
                exportAllData: true,
                exportMenu: [
                  {
                    label: "Export Table",
                    exportFunc: (cols, data) => {
                      setOpenExportDialog(true);

                      //  Flatten React element titles to plain strings for CSV export
                      const round2 = (v) =>
                        typeof v === "number" && !Number.isInteger(v) ? Math.round(v * 100) / 100 : v;

                      // year is now a real column, so drop the manual prepend:
                      const flattenedCols = cols.map((col) => ({
                        ...col,
                        title:
                          col.title && typeof col.title === "object" && col.title.props
                            ? col.title.props.children?.props?.children ??
                            col.title.props.children ??
                            col.field
                            : col.title,
                      }));

                      const dataOut = data.map((row) => row.map(round2));

                      setExportCols(flattenedCols);
                      setExportData(dataOut);

                      let filenameTemp = query.layerSpecs.layer
                        .replace(/-/g, "")
                        .replace(/\s+/g, "")
                        .replace(/\(+/g, "-")
                        .replace(/\)+/g, "");

                      setCsvFileNameTest(
                        filenameTemp +
                        "_" +
                        grouping.replace(/_/g, "") +
                        "_" +
                        getCurrentDate()
                      );
                    },
                  },
                ],
                padding: "dense",
                maxBodyHeight: 500,   // ~10 dense rows fit under this; header still sticks
                pageSize: 10,
                pageSizeOptions: [10, 25, 50, 100],

              }}
              icons={TABLE_ICONS}
              actions={[
                {
                  tooltip: "Select Grouping",
                  hidden:
                    query.layerSpecs.category === "Top 100",
                  icon: () => {
                    return (
                      <FormControl className={classes.formControl}>
                        <InputLabel id="grouping-label" htmlFor="show-grouping">Group By</InputLabel>
                        <Select
                          labelId="grouping-label"
                          id="grouping-id"
                          value={grouping}
                          onChange={handleChange}
                          inputProps={{
                            id: "show-grouping",
                            "aria-label": "grouping-select"
                          }}
                        >
                          <MenuItem value="route_id">Route ID</MenuItem>
                          <MenuItem value="ownership">Ownership</MenuItem>
                          <MenuItem value="f_system">Functional System</MenuItem>
                          <MenuItem value="urban_code">Urban Code</MenuItem>
                          <MenuItem value="nhs">
                            National Highway System (NHS)
                          </MenuItem>
                          <MenuItem value="strahnet">
                            Strategic Highway Network (STRAHNET)
                          </MenuItem>
                          <MenuItem value="truck">
                            National Truck Network (NN)
                          </MenuItem>
                          <MenuItem value="county_cod">County</MenuItem>
                          <MenuItem value="total">Total</MenuItem>
                        </Select>
                      </FormControl>
                    );
                  },
                  onClick: () => { },
                  isFreeAction: true,
                },

                (rowData) => {
                  let index = selectedRows.filter(
                    (item) => item.id === rowData.id
                  ).length;

                  return {
                    icon:
                      index > 0
                        ? () => (
                          <div style={{ lineHeight: 0 }}>
                            <p style={{ fontSize: 12 }}>Hide</p>
                            <MapIcon style={{ color: "green" }} />
                          </div>
                        )
                        : () => (
                          <div style={{ lineHeight: 0 }}>
                            <p style={{ fontSize: 12 }}>Show</p>
                            <MapIcon style={{ color: "red" }} />
                          </div>
                        ),
                    tooltip: index > 0 ? "Hide Map" : "Show Map",

                    onClick: (event, rowData) => {
                      let tempSelectedRows = [];
                      if (index === 0) {
                        tempSelectedRows = [...selectedRows, rowData];
                      } else {
                        tempSelectedRows = selectedRows.filter(
                          (item) => item.id !== rowData.id
                        );
                      }
                      handleClick(tempSelectedRows);
                    },
                  };
                },
              ]}

              components={{
                //accessibility fix for formlabel pagination select
                Pagination: (props) => (
                  <>
                    <label
                      htmlFor="pagination-rows-per-page"
                      style={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                        clip: "rect(0,0,0,0)",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Rows per page
                    </label>
                    <TablePagination
                      {...props}
                      SelectProps={{
                        inputProps: {
                          id: "pagination-rows-per-page",
                          "aria-label": "rows per page",
                        },
                      }}
                    />
                  </>
                ),
                //accessibility fix for empty table header
                Header: (props) => {
                  // Clone and patch headerProps to add aria-label to empty <th> cells
                  const patchedColumns = props.columns.map((col) => {
                    if (!col.title || col.title === "") {
                      return {
                        ...col,
                        tableData: {
                          ...col.tableData,
                          headerProps: {
                            ...(col.tableData?.headerProps || {}),
                            "aria-label": col.field === "actions" ? "Row actions" : "Column",
                          },
                        },
                      };
                    }
                    return col;
                  });
                  return <MTableHeader {...props} columns={patchedColumns} />;
                },

                Action: (props) => {
                  // only patch row-level icon actions (Show/Hide Map)
                  if (typeof props.action.icon === "function" && !props.action.isFreeAction) {
                    return (
                      <button
                        onClick={(e) => props.action.onClick(e, props.data)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            props.action.onClick(e, props.data);
                          }
                        }}
                        tabIndex={0}
                        aria-label={props.action.tooltip || "row action"}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          borderRadius: "4px",
                        }}
                        onFocus={(e) => (e.currentTarget.style.outline = "2px solid #1976d2")}
                        onBlur={(e) => (e.currentTarget.style.outline = "none")}
                      >
                        {props.action.icon()}
                      </button>
                    );
                  }
                  // fall back to default for free actions (column selector, group by)
                  return <MTableAction {...props} />;
                },
              }}
            />
          </TableTopScrollbar>
          <ExportFileNameDialog
            openExportDialog={openExportDialog}
            setOpenExportDialog={setOpenExportDialog}
            csvFileNameTest={csvFileNameTest}
            setCsvFileNameTest={setCsvFileNameTest}
            exportCols={exportCols}
            exportData={exportData}
          />
        </Paper>
      )}
    </div>
  );
}